import { films as rawFilms } from "@/mocks/films";
import { collections as rawCollections } from "@/mocks/collections";
import type { Collection, Film, FilmFilters, FilmType } from "@/types/film";

export const films: Film[] = rawFilms.map((item) => ({
  ...item,
  type: item.type === "tv" ? "tv" : "movies",
}));

export const collections: Collection[] = rawCollections.map((item) => ({ ...item }));

export const PAGE_SIZE = 9;

export const RATING_OPTIONS = [
  { label: "全部评分", value: "all" },
  { label: "9.0 分以上", value: "9" },
  { label: "8.0 分以上", value: "8" },
  { label: "7.0 分以上", value: "7" },
  { label: "6.0 分以上", value: "6" },
];

export const SORT_OPTIONS = [
  { label: "按发布时间", value: "latest" },
  { label: "按热门度", value: "popularity" },
  { label: "按评分", value: "rating" },
];

export function getFilmById(id: string): Film | undefined {
  return films.find((film) => film.id === id);
}

export function getFilmsByType(type: FilmType): Film[] {
  return films.filter((film) => film.type === type);
}

export function getFeatured(limit = 4): Film[] {
  return films
    .filter((film) => film.featured)
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, limit);
}

export function getLatest(limit = 6): Film[] {
  return [...films]
    .sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1))
    .slice(0, limit);
}

export function getPopular(limit = 4): Film[] {
  return [...films].sort((a, b) => b.popularity - a.popularity).slice(0, limit);
}

export function getYears(type?: FilmType): string[] {
  const source = type ? getFilmsByType(type) : films;
  return Array.from(new Set(source.map((film) => String(film.year)))).sort(
    (a, b) => Number(b) - Number(a),
  );
}

export function getRegions(type?: FilmType): string[] {
  const source = type ? getFilmsByType(type) : films;
  return Array.from(new Set(source.map((film) => film.region)));
}

export function applyFilters(filters: FilmFilters, source: Film[] = films): Film[] {
  let result = [...source];

  if (filters.type) {
    result = result.filter((film) => film.type === filters.type);
  }
  if (filters.year && filters.year !== "all") {
    result = result.filter((film) => String(film.year) === filters.year);
  }
  if (filters.region && filters.region !== "all") {
    result = result.filter((film) => film.region === filters.region);
  }
  if (filters.rating && filters.rating !== "all") {
    const threshold = Number(filters.rating);
    result = result.filter((film) => film.rating >= threshold);
  }

  const sort = filters.sort ?? "latest";
  if (sort === "latest") {
    result.sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
  } else if (sort === "popularity") {
    result.sort((a, b) => b.popularity - a.popularity);
  } else if (sort === "rating") {
    result.sort((a, b) => b.rating - a.rating);
  }

  return result;
}

export function searchFilms(keyword: string): Film[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return films.filter((film) => {
    const bucket = [
      film.title,
      film.director,
      film.summary,
      film.region,
      ...film.cast,
      ...film.genres,
    ]
      .join(" ")
      .toLowerCase();
    return bucket.includes(key);
  });
}

export function sortByPopularity(source: Film[]): Film[] {
  return [...source].sort((a, b) => b.popularity - a.popularity);
}

export function getRelated(film: Film, limit = 4): Film[] {
  return films
    .filter((item) => item.id !== film.id)
    .map((item) => {
      const sharedGenres = item.genres.filter((genre) => film.genres.includes(genre)).length;
      const sameType = item.type === film.type ? 2 : 0;
      const sameRegion = item.region === film.region ? 1 : 0;
      return { item, score: sharedGenres * 3 + sameType + sameRegion };
    })
    .sort((a, b) => b.score - a.score || b.item.popularity - a.item.popularity)
    .map((entry) => entry.item)
    .slice(0, limit);
}

export function paginate<T>(list: T[], page: number, pageSize = PAGE_SIZE): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function totalPages(count: number, pageSize = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(count / pageSize));
}